import { CombinationsConfiguratorFormData } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';
import { WindowElement } from '@/interfaces/components/combinations/WindowElement';
import i18n from '@/i18n';
import { JointsParameterRow } from '@/modules/ConfiguratorJoints/helpers/JointsConfiguration';
import { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import { UpdateProductFormEntry } from '@/interfaces/components/configurator/UpdateProductFormEntry';
import { CombinationsConfigurationItem } from '@/modules/ConfiguratorCombinations/helpers/CombinationsConfiguration';

// =====================================================================
// Stateless helpers extracted from CombinationsConfiguratorSelectorPopup
// =====================================================================

export const HERO_PRODUCT_NAMES = ['HERO.1', 'HERO.2', 'HERO.5'];

export function isHeroProductName(productName: string): boolean {
    return HERO_PRODUCT_NAMES.includes(productName);
}

export function getFieldValueByPID(
    rows: CombinationsConfiguratorFormData[],
    productFormId: number,
    fieldPID: string,
): string | number | boolean | undefined {
    return rows
        .find((row) => row.activeProductFormId === productFormId)
        ?.formData.find((field) => field.fieldPID === fieldPID)?.fieldValue;
}

export function getWindowDriveValue(
    rows: CombinationsConfiguratorFormData[],
    productFormId: number,
    drivePID: string,
): string {
    const value = getFieldValueByPID(rows, productFormId, drivePID);

    return typeof value === 'string' ? value : '';
}

export function getCombinedWidth({
    rows,
    selectedIds,
    widthPID,
    finalWidthPID,
    dimensionsTypePID,
}: {
    rows: CombinationsConfiguratorFormData[];
    selectedIds: number[];
    widthPID: string;
    finalWidthPID: string;
    dimensionsTypePID: string;
}): number {
    let total = 0;
    const dimensionTypeValueUsedForTotalWidth = 'Dimenzije oken';

    selectedIds.forEach((productId) => {
        const width = getFieldValueByPID(rows, productId, widthPID);
        const finalWidth = getFieldValueByPID(rows, productId, finalWidthPID);
        const dimensionsType = getFieldValueByPID(rows, productId, dimensionsTypePID);

        if (dimensionsType === dimensionTypeValueUsedForTotalWidth && finalWidth !== undefined) {
            total += Number(finalWidth);
            return;
        }

        if (width !== undefined) {
            total += Number(width);
            return;
        }
    });

    return roundToTwoDecimalsIfNeeded(total);
}

function roundToTwoDecimalsIfNeeded(value: number): number {
    return Number.isInteger(value) ? value : Number(value.toFixed(2));
}

export function getWidthDifference(totalWidth: number, combinedWidth: number): number {
    if (totalWidth <= combinedWidth) {
        return 0;
    }

    return totalWidth - combinedWidth;
}

export function areWindowsOfSameHeight(
    rows: CombinationsConfiguratorFormData[],
    idA: number,
    idB: number,
    heightPID: string,
): boolean {
    const a = getFieldValueByPID(rows, idA, heightPID);
    const b = getFieldValueByPID(rows, idB, heightPID);

    if (a === undefined || b === undefined) {
        return false;
    }

    return a === b;
}

export function getPositionDropdownOptions(selectedCount: number): Array<{ label: string; value: string }> {
    if (selectedCount === 2) {
        return [
            { label: '1', value: '1' },
            { label: '2', value: '2' },
        ];
    }

    if (selectedCount === 3) {
        return [
            { label: '1', value: '1' },
            { label: '2', value: '2' },
            { label: '3', value: '3' },
        ];
    }

    return [];
}

export function getNoChoiceGuideValue(checklistFieldConfig: any): string {
    return checklistFieldConfig?.LEFT_GUIDE_PID?.noChoiceValue ?? 'Brez';
}

export function getTwoWindowCombinationType(driveOne: string, driveTwo: string, isFirstGuideSingle: boolean): string {
    if (isFirstGuideSingle) {
        return 'D1';
    }

    const drivePatterns = [
        { pattern: () => driveOne === 'L' && driveTwo === 'L', type: 'D2' },
        { pattern: () => driveOne === 'L' && driveTwo === 'D', type: 'D2' },
        { pattern: () => driveOne === 'D' && driveTwo === 'D', type: 'D3' },
    ];

    const match = drivePatterns.find(({ pattern }) => pattern());
    return match ? match.type : '';
}

export interface ThreeWindowGuideFlags {
    areBothGuidesSingle: boolean;
    isSinglePartitionCombination: boolean;
    isPartitionSingleCombination: boolean;
    areBothGuidesPartition: boolean;
}

export function getThreeWindowCombinationType(
    driveOne: string,
    driveTwo: string,
    driveThree: string,
    flags: ThreeWindowGuideFlags,
): string {
    if (flags.areBothGuidesSingle) {
        return 'T1';
    }

    const isAnySinglePartitionCombination = flags.isSinglePartitionCombination || flags.isPartitionSingleCombination;

    const threeWindowPatterns = [
        // T2 patterns
        {
            patterns: [
                () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'L' && isAnySinglePartitionCombination,
                () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'D' && isAnySinglePartitionCombination,
                () => driveOne === 'D' && driveTwo === 'L' && driveThree === 'L' && flags.isSinglePartitionCombination,
                () => driveOne === 'L' && driveTwo === 'D' && driveThree === 'L' && flags.isPartitionSingleCombination,
            ],
            type: 'T2',
        },
        // T3 patterns
        {
            patterns: [
                () => driveOne === 'L' && driveTwo === 'D' && driveThree === 'D' && isAnySinglePartitionCombination,
                () => driveOne === 'D' && driveTwo === 'L' && driveThree === 'D' && flags.isSinglePartitionCombination,
                () => driveOne === 'D' && driveTwo === 'D' && driveThree === 'D' && isAnySinglePartitionCombination,
                () => driveOne === 'D' && driveTwo === 'D' && driveThree === 'L' && flags.isPartitionSingleCombination,
            ],
            type: 'T3',
        },
        // T4 patterns
        {
            patterns: [
                () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'L' && flags.areBothGuidesPartition,
            ],
            type: 'T4',
        },
        // T5 patterns
        {
            patterns: [
                () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'D' && flags.areBothGuidesPartition,
                () => driveOne === 'L' && driveTwo === 'D' && driveThree === 'D' && flags.areBothGuidesPartition,
            ],
            type: 'T5',
        },
        // T6 patterns
        {
            patterns: [
                () => driveOne === 'D' && driveTwo === 'D' && driveThree === 'D' && flags.areBothGuidesPartition,
            ],
            type: 'T6',
        },
    ];

    for (const { patterns, type } of threeWindowPatterns) {
        if (patterns.some((pattern) => pattern())) {
            return type;
        }
    }

    return '';
}

export interface InvalidCombinationParams {
    isFirstGuideSingle: boolean;
    isSecondGuideSingle: boolean;
    isFirstGuidePartition: boolean;
    isSecondGuidePartition: boolean;
    selectedCount: number;
    firstWindowDrive: string;
    secondWindowDrive: string;
    thirdWindowDrive: string;
}

export function isCombinationInvalid(params: InvalidCombinationParams): boolean {
    if ((params.isFirstGuideSingle && params.isSecondGuideSingle) || params.selectedCount === 0) {
        return false;
    }

    if (params.isFirstGuidePartition && params.firstWindowDrive === 'D' && params.secondWindowDrive === 'L') {
        return true;
    }

    if (
        params.isSecondGuidePartition &&
        params.selectedCount === 3 &&
        params.secondWindowDrive === 'D' &&
        params.thirdWindowDrive === 'L'
    ) {
        return true;
    }

    return false;
}

export function computeGuideValueForSegment(
    selectedCount: number,
    requiredCount: number,
    sameHeight: boolean,
    guideType: string,
): string {
    if (selectedCount < requiredCount) {
        return '';
    }

    return sameHeight ? guideType : 'Enojno';
}

export function computeAdjacentLeftGuideValue(rightGuideValue: string, noChoiceGuideValue: string): string {
    if (rightGuideValue === 'Pregradno') {
        return noChoiceGuideValue;
    }

    return rightGuideValue;
}

export function isAssemblyStep1Completed(selectedCount: number): boolean {
    return selectedCount >= 2;
}

export function isAssemblyStep2Completed(
    filteredRows: CombinationsConfiguratorFormData[],
    positionPID: string,
    step1Completed: boolean,
): boolean {
    if (!step1Completed) return false;

    const allHavePositions = filteredRows.every((product) => {
        const positionField = product.formData.find((field) => field.fieldPID === positionPID);
        return !!positionField?.fieldValue;
    });

    if (!allHavePositions) return false;

    const positionValues = filteredRows.map((product) => {
        const positionField = product.formData.find((field) => field.fieldPID === positionPID);
        return positionField?.fieldValue;
    });

    return new Set(positionValues).size === positionValues.length;
}

export function isAssemblyStep3Completed(totalWidth: number, step2Completed: boolean): boolean {
    if (!step2Completed) return false;

    return totalWidth > 0;
}

export function isAssemblyStep4Completed(
    activeStepsCount: number,
    step3Completed: boolean,
    guideType: string,
    filteredRows: CombinationsConfiguratorFormData[],
    drivePID: string,
): boolean {
    if (activeStepsCount < 4) return true;

    if (!step3Completed) return false;
    if (!guideType) return false;

    return filteredRows.every((product) => {
        const driveField = product.formData.find((field) => field.fieldPID === drivePID);
        return !!driveField?.fieldValue;
    });
}

export function computeLargestRollerShutterBoxSize(
    rows: CombinationsConfiguratorFormData[],
    selectedIds: number[],
    boxSizePID: string,
    selectedBoxSizePID: string,
    boxTypePID?: string,
    fieldIdentifier: 'fieldPID' | 'key' = 'fieldPID',
): { boxSize: number | null; boxType: number | null } {
    if (selectedIds.length === 0) {
        return { boxSize: null, boxType: null };
    }

    const productBoxSizes = selectedIds.map((productId) => {
        const productRow = rows.find((row) => row.activeProductFormId === productId);
        const boxSizeField = productRow?.formData.find((field) => field[fieldIdentifier] === boxSizePID);
        const selectedBoxSizeField = productRow?.formData.find(
            (field) => field[fieldIdentifier] === selectedBoxSizePID,
        );

        return {
            productId,
            boxSizeValue: boxSizeField?.fieldValue || '',
            selectedBoxSizeValue: selectedBoxSizeField?.fieldValue || '',
        };
    });

    const numericBoxSizes = productBoxSizes
        .map((product) => {
            const boxValue = parseFloat(String(product.boxSizeValue));
            const selectedBoxValue = parseFloat(String(product.selectedBoxSizeValue));
            const validNumValue = isNaN(boxValue) ? selectedBoxValue : boxValue;

            return { productId: product.productId, value: validNumValue };
        })
        .filter((item) => !isNaN(item.value));

    const boxSize =
        numericBoxSizes.length > 0
            ? numericBoxSizes.reduce((max, current) => (current.value > max.value ? current : max)).value
            : null;

    let boxType: number | null = null;

    // We need this for HERO-1 because console type is numerical
    if (boxTypePID) {
        const numericBoxTypes = selectedIds
            .map((productId) => {
                const productRow = rows.find((row) => row.activeProductFormId === productId);
                const boxTypeField = productRow?.formData.find((field) => field[fieldIdentifier] === boxTypePID);
                return parseFloat(String(boxTypeField?.fieldValue || ''));
            })
            .filter((value) => !isNaN(value));

        boxType = numericBoxTypes.length > 0 ? Math.max(...numericBoxTypes) : null;
    }

    return {
        boxSize: typeof boxSize === 'number' ? boxSize : null,
        boxType: typeof boxType === 'number' ? boxType : null,
    };
}

export function computeMaxRollerShutterBoxAndSelectedBox(
    rows: CombinationsConfiguratorFormData[],
    selectedIds: number[],
    boxSizePID: string,
    selectedBoxSizePID: string,
    fieldIdentifier: 'fieldPID' | 'key' = 'fieldPID',
): { maxBoxSize: number | null; maxSelectedBoxSize: number | null } {
    const boxSizeValues: number[] = [];
    const selectedBoxSizeValues: number[] = [];

    selectedIds.forEach((productId) => {
        const productRow = rows.find((row) => row.activeProductFormId === productId);
        const boxSizeField = productRow?.formData.find((field) => field[fieldIdentifier] === boxSizePID);
        const selectedBoxSizeField = productRow?.formData.find(
            (field) => field[fieldIdentifier] === selectedBoxSizePID,
        );

        const boxValue = parseFloat(String(boxSizeField?.fieldValue || ''));
        const selectedValue = parseFloat(String(selectedBoxSizeField?.fieldValue || ''));

        if (!isNaN(boxValue)) boxSizeValues.push(boxValue);
        if (!isNaN(selectedValue)) selectedBoxSizeValues.push(selectedValue);
    });

    return {
        maxBoxSize: boxSizeValues.length > 0 ? Math.max(...boxSizeValues) : null,
        maxSelectedBoxSize: selectedBoxSizeValues.length > 0 ? Math.max(...selectedBoxSizeValues) : null,
    };
}

export function sortSelectedIdsByPosition(
    rows: CombinationsConfiguratorFormData[],
    selectedIds: number[],
    positionPID: string,
): number[] {
    return [...selectedIds].sort((a, b) => {
        const aPositionField = rows
            .find((row) => row.activeProductFormId === a)
            ?.formData.find((field) => field.fieldPID === positionPID);
        const bPositionField = rows
            .find((row) => row.activeProductFormId === b)
            ?.formData.find((field) => field.fieldPID === positionPID);

        if (!aPositionField || !bPositionField) {
            return 0;
        }

        const aPos = Number(aPositionField.fieldValue);
        const bPos = Number(bPositionField.fieldValue);

        return aPos - bPos;
    });
}

/**
 * Orders window elements so that each one with a valid 1-based `canvasConfig.position`
 * claims that slot. Elements without a valid position fill the remaining slots in their
 * original order.
 */
export function sortWindowElementsByPosition(items: WindowElement[]): WindowElement[] {
    const claimed: WindowElement[] = Array.from({ length: items.length });
    const unclaimed: WindowElement[] = [];

    items.forEach((item) => {
        const slot = parseInt(item.canvasConfig.position as any, 10) - 1;
        const canClaimSlot = slot >= 0 && slot < items.length && !claimed[slot];

        if (canClaimSlot) {
            claimed[slot] = item;
        } else {
            unclaimed.push(item);
        }
    });

    return claimed.map((item) => item ?? unclaimed.shift()!);
}

/**
 * Downloads the given Konva stage as a PNG file. Captures the entire stage so
 * that every layer/shape currently rendered in the canvas is encapsulated in
 * the resulting image.
 */
async function buildCombinedCanvas(
    stage: any,
    previewRows: JointsParameterRow[] = [],
    previewOptions: Array<{ label: string; isSelected: boolean }> = [],
): Promise<HTMLCanvasElement | null> {
    if (!stage || typeof stage.toCanvas !== 'function') {
        return null;
    }

    const pixelRatio = 2;
    const stageCanvas = stage.toCanvas({
        pixelRatio,
    });
    const extraStageWidth = 130;
    const previewCanvas = previewRows.length
        ? renderPreviewRowsToCanvas([...previewRows, ...getPreviewOptionRows(previewOptions)], pixelRatio)
        : null;
    const canvas = document.createElement('canvas');
    const previewGap = previewCanvas ? 64 : 0;
    const stageOffsetX = previewCanvas ? previewCanvas.width + previewGap : 0;
    canvas.width = stageOffsetX + stageCanvas.width + extraStageWidth;
    canvas.height = Math.max(stageCanvas.height, previewCanvas?.height ?? 0);

    const context = canvas.getContext('2d');
    if (!context) {
        return null;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    if (previewCanvas) {
        context.drawImage(previewCanvas, 0, 0);
    }
    context.drawImage(stageCanvas, stageOffsetX, 0);

    return canvas;
}

export async function downloadStageAsPng(
    stage: any,
    fileName: string = 'combined-box.png',
    previewRows: JointsParameterRow[] = [],
    previewOptions: Array<{ label: string; isSelected: boolean }> = [],
): Promise<void> {
    const canvas = await buildCombinedCanvas(stage, previewRows, previewOptions);
    if (!canvas) {
        return;
    }

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = fileName.endsWith('.png') ? fileName : `${fileName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export async function getStageAsPngBase64(
    stage: any,
    previewRows: JointsParameterRow[] = [],
    previewOptions: Array<{ label: string; isSelected: boolean }> = [],
): Promise<string | null> {
    const canvas = await buildCombinedCanvas(stage, previewRows, previewOptions);
    return canvas ? canvas.toDataURL('image/png') : null;
}

const previewRowStyle = {
    width: 430,
    height: 50,
    additionalOptionsBaseHeight: 60,
    extraOptionHeight: 25,
    gap: 12,
    padding: 16,
    labelWidth: 145,
    border: '#d8dee7',
    label: 'rgba(32, 54, 77, 0.62)',
    value: '#20364d',
};

type PreviewCanvasRow = JointsParameterRow & { optionCount?: number };

function getPreviewOptionRows(options: Array<{ label: string; isSelected: boolean }>): PreviewCanvasRow[] {
    const selectedOptionLabels = options
        .filter((option) => option.isSelected)
        .map((option) => translatePreviewText(option.label));

    if (!selectedOptionLabels.length) {
        return [];
    }

    return [
        {
            label: 'Additional options',
            value: selectedOptionLabels.join(', '),
            optionCount: selectedOptionLabels.length,
        },
    ];
}

function renderPreviewRowsToCanvas(rows: PreviewCanvasRow[], pixelRatio: number): HTMLCanvasElement | null {
    const canvas = document.createElement('canvas');
    canvas.width = previewRowStyle.width * pixelRatio;

    let canvasHeight = previewRowStyle.padding * 2;
    rows.forEach((row, index) => {
        canvasHeight += getPreviewRowHeight(row);

        if (index < rows.length - 1) {
            canvasHeight += previewRowStyle.gap;
        }
    });

    canvas.height = canvasHeight * pixelRatio;

    const context = canvas.getContext('2d');

    if (!context) {
        return null;
    }

    context.scale(pixelRatio, pixelRatio);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, previewRowStyle.width, canvas.height / pixelRatio);

    let currentY = previewRowStyle.padding;

    rows.forEach((row, index) => {
        const x = previewRowStyle.padding;
        const y = currentY;
        const width = previewRowStyle.width - previewRowStyle.padding * 2;
        const rowHeight = getPreviewRowHeight(row);

        context.fillStyle = '#ffffff';
        context.strokeStyle = previewRowStyle.border;
        context.lineWidth = 1;
        context.fillRect(x, y, width, rowHeight);
        context.strokeRect(x, y, width, rowHeight);

        context.textBaseline = 'top';
        context.font = '600 14px Arial';
        context.fillStyle = previewRowStyle.label;
        drawWrappedText(context, translatePreviewText(row.label), x + 14, y + 19, previewRowStyle.labelWidth, 18);

        drawPreviewRowValue(context, row, x + previewRowStyle.labelWidth + 24, y + 12, width);

        currentY += rowHeight + previewRowStyle.gap;
    });

    return canvas;
}

function getPreviewRowHeight(row: PreviewCanvasRow): number {
    const measureCanvas = document.createElement('canvas');
    const measureContext = measureCanvas.getContext('2d');

    if (!measureContext) {
        return previewRowStyle.height;
    }

    measureContext.font = '700 15px Arial';

    const lineHeight = 19;
    const valueWidth = previewRowStyle.width - previewRowStyle.padding * 2 - previewRowStyle.labelWidth - 38;

    if (row.values?.length) {
        const pairValueWidth = getPreviewPairValueWidth(valueWidth, row.values.length);
        const groupedTextHeight = Math.max(
            ...row.values.map((value) =>
                measureWrappedTextHeight(measureContext, value.value, pairValueWidth, lineHeight),
            ),
        );

        return Math.max(previewRowStyle.height, groupedTextHeight + 40);
    }
    const textHeight = measureWrappedTextHeight(measureContext, row.value, valueWidth, lineHeight);

    return Math.max(previewRowStyle.height, textHeight + 36);
}

function drawPreviewRowValue(
    context: CanvasRenderingContext2D,
    row: PreviewCanvasRow,
    x: number,
    y: number,
    rowWidth: number,
) {
    const valueWidth = rowWidth - previewRowStyle.labelWidth - 38;

    if (!row.values?.length) {
        context.font = '700 15px Arial';
        context.fillStyle = previewRowStyle.value;
        drawWrappedText(context, translatePreviewText(row.value), x, y + 6, valueWidth, 19);
        return;
    }

    const pairGap = 14;
    const pairWidth = getPreviewPairValueWidth(valueWidth, row.values.length);

    row.values.forEach((value, index) => {
        const pairX = x + index * (pairWidth + pairGap);

        context.font = '600 12px Arial';
        context.fillStyle = previewRowStyle.label;
        drawWrappedText(context, translatePreviewText(value.label), pairX, y, pairWidth, 16);

        context.font = '700 15px Arial';
        context.fillStyle = previewRowStyle.value;
        drawWrappedText(context, translatePreviewText(value.value), pairX, y + 17, pairWidth, 19);
    });
}

function translatePreviewText(text: string): string {
    const hasTranslation = typeof (i18n as any).te === 'function' ? (i18n as any).te(text) : false;

    return hasTranslation ? (i18n.t(text) as string) : text;
}

function getPreviewPairValueWidth(valueWidth: number, valueCount: number): number {
    const pairGap = 14;

    return (valueWidth - pairGap * (valueCount - 1)) / valueCount;
}

function renderElementFooterToCanvas(element: HTMLElement, pixelRatio: number): HTMLCanvasElement | null {
    const bounds = element.getBoundingClientRect();
    if (bounds.width === 0 || bounds.height === 0) {
        return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(bounds.width * pixelRatio);
    canvas.height = Math.ceil(bounds.height * pixelRatio);
    const context = canvas.getContext('2d');
    if (!context) {
        return null;
    }

    context.scale(pixelRatio, pixelRatio);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, bounds.width, bounds.height);

    Array.from(element.querySelectorAll<HTMLElement>('.joints-options-icons__item')).forEach((item) => {
        const itemBounds = item.getBoundingClientRect();
        const itemStyle = window.getComputedStyle(item);
        const x = itemBounds.left - bounds.left;
        const y = itemBounds.top - bounds.top;

        context.fillStyle = itemStyle.backgroundColor;
        context.strokeStyle = itemStyle.borderColor;
        context.lineWidth = parseFloat(itemStyle.borderWidth) || 1;
        context.fillRect(x, y, itemBounds.width, itemBounds.height);
        context.strokeRect(x, y, itemBounds.width, itemBounds.height);

        const image = item.querySelector<HTMLImageElement>('img');
        if (image) {
            const imageBounds = image.getBoundingClientRect();
            context.drawImage(
                image,
                imageBounds.left - bounds.left,
                imageBounds.top - bounds.top,
                imageBounds.width,
                imageBounds.height,
            );
        }

        const label = item.querySelector<HTMLElement>('.joints-options-icons__label');
        if (label) {
            const labelBounds = label.getBoundingClientRect();
            const labelStyle = window.getComputedStyle(label);
            context.fillStyle = labelStyle.color;
            context.font = `${labelStyle.fontWeight} ${labelStyle.fontSize} ${labelStyle.fontFamily}`;
            context.textBaseline = 'top';
            const lineHeight =
                Number.parseFloat(labelStyle.lineHeight) || Number.parseFloat(labelStyle.fontSize) * 1.25;
            drawWrappedText(
                context,
                label.innerText,
                labelBounds.left - bounds.left,
                labelBounds.top - bounds.top,
                labelBounds.width,
                lineHeight,
            );
        }
    });

    return canvas;
}

function drawWrappedText(
    context: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
) {
    const words = text.split(/\s+/).filter(Boolean);
    let line = '';
    let currentY = y;

    for (const word of words) {
        const nextLine = line ? `${line} ${word}` : word;
        if (context.measureText(nextLine).width <= maxWidth) {
            line = nextLine;
            continue;
        }

        if (line) {
            context.fillText(line, x, currentY);
            currentY += lineHeight;
            line = word;
            continue;
        }

        const splitWordLines = splitLongWord(context, word, maxWidth);
        splitWordLines.slice(0, -1).forEach((part) => {
            context.fillText(part, x, currentY);
            currentY += lineHeight;
        });
        line = splitWordLines[splitWordLines.length - 1] ?? '';
    }

    if (line) {
        context.fillText(line, x, currentY);
    }
}

function splitLongWord(context: CanvasRenderingContext2D, word: string, maxWidth: number): string[] {
    const lines: string[] = [];
    let segment = '';

    for (const character of word) {
        const nextSegment = `${segment}${character}`;
        if (segment && context.measureText(nextSegment).width > maxWidth) {
            lines.push(segment);
            segment = character;
            continue;
        }

        segment = nextSegment;
    }

    if (segment) {
        lines.push(segment);
    }

    return lines;
}

function measureWrappedTextHeight(
    context: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    lineHeight: number,
): number {
    const words = text.split(/\s+/).filter(Boolean);
    let line = '';
    let lines = 1;

    for (const word of words) {
        const nextLine = line ? `${line} ${word}` : word;

        if (context.measureText(nextLine).width <= maxWidth) {
            line = nextLine;
            continue;
        }

        if (line) {
            lines++;
            line = word;
            continue;
        }

        const splitLines = splitLongWord(context, word, maxWidth);
        lines += splitLines.length - 1;
        line = splitLines[splitLines.length - 1] ?? '';
    }

    return lines * lineHeight;
}

function getConfigurationPid(currentConfig: CombinationsConfigurationItem, fieldName: string): string {
    return String(currentConfig.checklistFields[fieldName]?.pid ?? '');
}

export function resetCombinationSwitchField(
    row: ProductRowItem,
    currentConfig: CombinationsConfigurationItem | null,
    updateProductFormValue: (entry: UpdateProductFormEntry) => void,
): void {
    if (!currentConfig) {
        return;
    }

    const positionPid = getConfigurationPid(currentConfig, 'COMBINATIONS_PID');
    updateProductFormValue({ pId: positionPid, value: false, productFormId: row.activeProductFormId });
}
